// Toast.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  Check, 
  Info, 
  AlertTriangle, 
  XCircle,
  X,
  AlertCircle,
  Loader2,
} from 'lucide-react';

// Context
const ToastContext = createContext();

// Hook
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

// Toast Provider Component
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const raw = typeof toast === 'string' ? { message: toast } : toast || {};
    const id = raw.id || Math.random().toString(36).substring(2, 9);
    const type = raw.type || 'info';
    const title = raw.title || (
      type === 'success' ? 'Success' :
      type === 'error' ? 'Error' :
      type === 'warn' ? 'Warning' :
      type === 'loading' ? 'Processing' : 'Information'
    );
    const message = raw.message || '';
    const duration = type === 'loading' ? 0 : (raw.duration ?? 4000);

    setToasts((prev) => {
      const index = prev.findIndex((t) => t.id === id);
      if (index !== -1) {
        const updated = [...prev];
        updated[index] = { ...prev[index], ...raw, id, type, title, message, duration };
        return updated;
      }
      return [...prev, { ...raw, id, type, title, message, duration }];
    });

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

// Toast Container Component
const ToastContainer = () => {
  const { toasts, removeToast } = useContext(ToastContext);
  if (!toasts || toasts.length === 0) return null;
  return (
    <div
      className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 w-80 pointer-events-none"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

// Toast Item Component
const ToastItem = ({ toast, onClose }) => {
  const { type = 'info', title, message, duration = 4000 } = toast;
  const [isPaused, setIsPaused] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 200);
  }, [onClose]);

  useEffect(() => {
    if (duration > 0 && !isPaused) {
      const timer = setTimeout(handleClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, isPaused, handleClose]);

  const config = {
    loading: {
      gradient: "from-blue-600 via-indigo-500 to-purple-500",
      iconColor: "text-[#3538CD]",
      iconBg: "bg-indigo-50 border border-indigo-100",
      icon: Loader2,
      isSpinning: true,
    },
    success: {
      gradient: "from-emerald-500 via-teal-400 to-emerald-400",
      iconColor: "text-emerald-500",
      iconBg: "bg-emerald-50 border border-emerald-100",
      icon: Check,
    },
    error: {
      gradient: "from-rose-500 via-pink-500 to-rose-400",
      iconColor: "text-rose-500",
      iconBg: "bg-rose-50 border border-rose-100",
      icon: XCircle,
    },
    warn: {
      gradient: "from-amber-500 via-orange-400 to-amber-400",
      iconColor: "text-amber-500",
      iconBg: "bg-amber-50 border border-amber-100",
      icon: AlertTriangle,
    },
    info: {
      gradient: "from-indigo-600 via-blue-500 to-cyan-400",
      iconColor: "text-[#3538CD]",
      iconBg: "bg-indigo-50 border border-indigo-100",
      icon: Info,
    },
    secondary: {
      gradient: "from-slate-600 via-slate-500 to-slate-400",
      iconColor: "text-slate-600",
      iconBg: "bg-slate-100 border border-slate-200",
      icon: AlertCircle,
    },
    contrast: {
      gradient: "from-purple-500 via-indigo-500 to-blue-500",
      iconColor: "text-white",
      iconBg: "bg-slate-800 border border-slate-700",
      icon: AlertCircle,
    },
  };

  const style = config[type] || config.info;
  const IconComponent = style.icon;

  const displayMessage = message || title;
  const showTitle = title && message && title !== message;

  return (
    <div
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      className={`pointer-events-auto relative w-full overflow-hidden rounded-xl bg-white border border-slate-200/90 shadow-[0_8px_30px_rgb(0,0,0,0.08)] backdrop-blur-md transition-all duration-200 ${
        isExiting ? "opacity-0 translate-y-2 scale-95" : "animate-in slide-in-from-top-2 fade-in duration-200"
      }`}
    >
      <div className="flex items-center gap-3 px-3.5 py-3">
        {/* Minimal Icon */}
        <div className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-lg ${style.iconBg} ${style.iconColor}`}>
          <IconComponent className={`w-4 h-4 stroke-[2.5] ${style.isSpinning ? 'animate-spin' : ''}`} />
        </div>

        {/* Text Content */}
        <div className="flex-1 min-w-0">
          {showTitle ? (
            <div>
              <p className="text-xs font-semibold text-slate-800 leading-none mb-0.5">{title}</p>
              <p className="text-xs font-normal text-slate-500 leading-tight truncate">{message}</p>
            </div>
          ) : (
            <p className="text-xs font-medium text-slate-700 leading-tight">{displayMessage}</p>
          )}
        </div>

        {/* Close Button */}
        <button
          onClick={handleClose}
          className="flex-shrink-0 p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
          aria-label="Close notification"
        >
          <X className="w-3.5 h-3.5 stroke-[2]" />
        </button>
      </div>

      {/* Gradient Progress Loader Bar */}
      {duration > 0 && (
        <div className="h-[2px] w-full bg-slate-100 overflow-hidden">
          <div
            className={`h-full bg-gradient-to-r ${style.gradient}`}
            style={{
              animationName: 'toastProgress',
              animationDuration: `${duration}ms`,
              animationTimingFunction: 'linear',
              animationFillMode: 'forwards',
              animationPlayState: isPaused ? 'paused' : 'running',
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes toastProgress {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

// Demo Component for testing toast UI
export const ToastDemo = () => {
  const { addToast } = useToast();

  const toasts = [
    { type: 'success', title: 'Success', message: 'Operation completed successfully.' },
    { type: 'info', title: 'Info', message: 'Here is some information you should know.' },
    { type: 'warn', title: 'Warning', message: 'This action requires your attention.' },
    { type: 'error', title: 'Error', message: 'Something went wrong. Please try again.' },
    { type: 'loading', title: 'Processing', message: 'Updating records...' },
  ];

  return (
    <div className="p-8 bg-slate-50 min-h-screen">
      <div className="max-w-xl mx-auto space-y-4">
        <h1 className="text-2xl font-bold text-slate-800">Minimal Toast UI</h1>
        <div className="grid grid-cols-2 gap-3">
          {toasts.map((t) => (
            <button
              key={t.type}
              onClick={() => addToast(t)}
              className="p-3 bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md text-xs font-semibold text-slate-700 uppercase"
            >
              Show {t.type} Toast
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};